import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePlatformSettings } from '../../context/PlatformSettingsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Settings,
  ArrowLeft,
  Loader2,
  Save,
  Palette,
  Image as ImageIcon,
  Type,
  RefreshCw
} from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const SuperAdminSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshSettings } = usePlatformSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platform_name: 'WorkStream Pro',
    logo_url: '/logo512.png',
    primary_color: '#2563EB',
    secondary_color: '#1E293B',
    accent_color: '#06B6D4',
    stripe_api_key: '',
    stripe_webhook_secret: '',
    paypal_client_id: '',
    paypal_secret: '',
    paypal_mode: 'sandbox',
    sendgrid_api_key: '',
    sender_email: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/platform-settings');
      if (response.data) {
        setSettings({
          platform_name: response.data.platform_name || 'WorkStream Pro',
          logo_url: response.data.logo_url || '/logo512.png',
          primary_color: response.data.primary_color || '#2563EB',
          secondary_color: response.data.secondary_color || '#1E293B',
          accent_color: response.data.accent_color || '#06B6D4',
          stripe_api_key: response.data.stripe_api_key || '',
          stripe_webhook_secret: response.data.stripe_webhook_secret || '',
          paypal_client_id: response.data.paypal_client_id || '',
          paypal_secret: response.data.paypal_secret || '',
          paypal_mode: response.data.paypal_mode || 'sandbox',
          sendgrid_api_key: response.data.sendgrid_api_key || '',
          sender_email: response.data.sender_email || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Don't show error toast on first load, just use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/platform-settings', settings);
      toast.success('Platform settings saved successfully!');
      
      // Refresh the platform settings in context
      refreshSettings();
      
      // Reload to apply changes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      platform_name: 'WorkStream Pro',
      logo_url: '/logo512.png',
      primary_color: '#2563EB',
      secondary_color: '#1E293B',
      accent_color: '#06B6D4'
    });
    toast.info('Reset to default values. Click Save to apply.');
  };

  // Redirect non-super-admins
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400">Access denied. Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/super-admin')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-4xl font-bold uppercase tracking-tight text-white">
                Platform Settings
              </h1>
              <p className="text-slate-400 mt-1">Customize the Super Admin panel branding</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset to Default
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Platform Branding */}
        <div className="card-industrial">
          <div className="flex items-center gap-2 mb-6">
            <Type className="w-5 h-5 text-amber-500" />
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">
              Platform Branding
            </h2>
          </div>

          <div className="space-y-6">
            {/* Platform Name */}
            <div>
              <Label className="text-slate-400 mb-2 block">
                Platform Name
              </Label>
              <Input
                value={settings.platform_name}
                onChange={(e) => setSettings({...settings, platform_name: e.target.value})}
                placeholder="WorkStream Pro"
                className="input-industrial max-w-md"
              />
              <p className="text-slate-500 text-sm mt-1">
                This name appears in the navigation and throughout the Super Admin panel
              </p>
            </div>

            {/* Logo URL */}
            <div>
              <Label className="text-slate-400 mb-2 block">
                Logo
              </Label>
              <div className="flex gap-3 items-start">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Convert to base64
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setSettings({...settings, logo_url: reader.result});
                            toast.success('Logo uploaded! Click Save to apply.');
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="flex-1 text-white cursor-pointer"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSettings({...settings, logo_url: ''});
                        toast.info('Logo cleared. Click Save to apply.');
                      }}
                      className="whitespace-nowrap"
                    >
                      Clear
                    </Button>
                  </div>
                  <p className="text-slate-500 text-sm mt-2">
                    Upload your logo image (PNG, JPG, SVG recommended)
                  </p>
                </div>
                {settings.logo_url && (
                  <div className="w-24 h-24 bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-center overflow-hidden p-2">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{display: 'none'}} className="text-slate-500 text-xs text-center">
                      Invalid image
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Color Customization */}
        <div className="card-industrial">
          <div className="flex items-center gap-2 mb-6">
            <Palette className="w-5 h-5 text-amber-500" />
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">
              Color Scheme
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Primary Color */}
            <div>
              <Label className="text-slate-400 mb-2 block">
                Primary Color
              </Label>
              <div className="flex gap-3 items-center">
                <Input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                  className="w-20 h-12 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                  placeholder="#F59E0B"
                  className="input-industrial flex-1"
                />
              </div>
              <div 
                className="mt-3 h-12 rounded-lg border border-slate-700"
                style={{ backgroundColor: settings.primary_color }}
              />
              <p className="text-slate-500 text-sm mt-2">
                Used for highlights and accents
              </p>
            </div>

            {/* Secondary Color */}
            <div>
              <Label className="text-slate-400 mb-2 block">
                Secondary Color
              </Label>
              <div className="flex gap-3 items-center">
                <Input
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                  className="w-20 h-12 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                  placeholder="#1E293B"
                  className="input-industrial flex-1"
                />
              </div>
              <div 
                className="mt-3 h-12 rounded-lg border border-slate-700"
                style={{ backgroundColor: settings.secondary_color }}
              />
              <p className="text-slate-500 text-sm mt-2">
                Background and container colors
              </p>
            </div>

            {/* Accent Color */}
            <div>
              <Label className="text-slate-400 mb-2 block">
                Accent Color
              </Label>
              <div className="flex gap-3 items-center">
                <Input
                  type="color"
                  value={settings.accent_color}
                  onChange={(e) => setSettings({...settings, accent_color: e.target.value})}
                  className="w-20 h-12 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.accent_color}
                  onChange={(e) => setSettings({...settings, accent_color: e.target.value})}
                  placeholder="#22D3EE"
                  className="input-industrial flex-1"
                />
              </div>
              <div 
                className="mt-3 h-12 rounded-lg border border-slate-700"
                style={{ backgroundColor: settings.accent_color }}
              />
              <p className="text-slate-500 text-sm mt-2">
                Links and interactive elements
              </p>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="card-industrial">
          <div className="flex items-center gap-2 mb-6">
            <ImageIcon className="w-5 h-5 text-amber-500" />
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">
              Preview
            </h2>
          </div>

          <div className="bg-slate-900 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center gap-4 mb-6">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt="Logo" 
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div 
                  className="text-3xl font-bold"
                  style={{ 
                    background: `linear-gradient(to right, ${settings.primary_color}, ${settings.accent_color})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {settings.platform_name}
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full justify-start"
                style={{ backgroundColor: settings.primary_color }}
              >
                Primary Button Example
              </Button>
              <Button 
                variant="outline"
                className="w-full justify-start"
                style={{ borderColor: settings.accent_color, color: settings.accent_color }}
              >
                Accent Button Example
              </Button>
              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: settings.secondary_color }}
              >
                <p className="text-white">Card with secondary background color</p>
              </div>
            </div>
          </div>
          
          <p className="text-slate-400 text-sm mt-4">
            💡 Changes will be applied after saving and refreshing the page
          </p>
        </div>

        {/* Payment Gateway Configuration */}
        <div className="card-industrial">
          <div className="flex items-center gap-2 mb-6">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">
              Payment Gateway Configuration
            </h2>
          </div>

          <div className="space-y-8">
            {/* Stripe Configuration */}
            <div className="border border-slate-700 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Stripe</h3>
                  <p className="text-slate-400 text-sm">Credit card and online payments</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 mb-2 block">
                    Stripe API Key <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="password"
                    value={settings.stripe_api_key || ''}
                    onChange={(e) => setSettings({...settings, stripe_api_key: e.target.value})}
                    placeholder="sk_test_... or sk_live_..."
                    className="input-industrial font-mono text-sm"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Get your key from: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener" className="text-blue-400 hover:underline">Stripe Dashboard</a>
                  </p>
                </div>

                <div>
                  <Label className="text-slate-400 mb-2 block">
                    Stripe Webhook Secret
                  </Label>
                  <Input
                    type="password"
                    value={settings.stripe_webhook_secret || ''}
                    onChange={(e) => setSettings({...settings, stripe_webhook_secret: e.target.value})}
                    placeholder="whsec_..."
                    className="input-industrial font-mono text-sm"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    For webhook signature verification (optional but recommended)
                  </p>
                </div>
              </div>
            </div>

            {/* PayPal Configuration */}
            <div className="border border-slate-700 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-indigo-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.32 23.751c-.46 0-.883-.306-1.026-.76L3.278 11.578a1.167 1.167 0 011.11-1.526h4.177c3.894 0 6.243-1.884 6.243-5.018 0-1.022-.242-1.86-.7-2.487-.458-.627-1.162-1.031-2.052-1.202A7.518 7.518 0 0010.496.75h-.003c-.345 0-.676.029-.992.086-.316.057-.625.142-.925.254a7.476 7.476 0 00-1.722.858 7.436 7.436 0 00-1.444 1.368 7.4 7.4 0 00-1.052 1.746c-.258.639-.43 1.328-.51 2.055L.066 21.955a1.167 1.167 0 001.11 1.526h6.118c.465 0 .883-.306 1.026-.76zm11.373-11.167h-4.177l-3.016 10.5a1.167 1.167 0 001.11 1.526h4.177c3.894 0 6.243-1.884 6.243-5.018 0-3.134-2.35-5.008-6.337-5.008z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">PayPal</h3>
                  <p className="text-slate-400 text-sm">Alternative payment method</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 mb-2 block">
                    PayPal Client ID <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={settings.paypal_client_id || ''}
                    onChange={(e) => setSettings({...settings, paypal_client_id: e.target.value})}
                    placeholder="Your PayPal Client ID"
                    className="input-industrial font-mono text-sm"
                  />
                </div>

                <div>
                  <Label className="text-slate-400 mb-2 block">
                    PayPal Secret <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="password"
                    value={settings.paypal_secret || ''}
                    onChange={(e) => setSettings({...settings, paypal_secret: e.target.value})}
                    placeholder="Your PayPal Secret"
                    className="input-industrial font-mono text-sm"
                  />
                </div>

                <div>
                  <Label className="text-slate-400 mb-2 block">
                    PayPal Mode
                  </Label>
                  <select
                    value={settings.paypal_mode || 'sandbox'}
                    onChange={(e) => setSettings({...settings, paypal_mode: e.target.value})}
                    className="input-industrial w-full"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="live">Live (Production)</option>
                  </select>
                  <p className="text-slate-500 text-xs mt-1">
                    Use sandbox mode for testing. Get credentials from: <a href="https://developer.paypal.com/" target="_blank" rel="noopener" className="text-indigo-400 hover:underline">PayPal Developer</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Email Configuration (SendGrid) */}
            <div className="border border-slate-700 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-cyan-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Email Service (SendGrid)</h3>
                  <p className="text-slate-400 text-sm">Configure email notifications</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 mb-2 block">
                    SendGrid API Key <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="password"
                    value={settings.sendgrid_api_key || ''}
                    onChange={(e) => setSettings({...settings, sendgrid_api_key: e.target.value})}
                    placeholder="SG...."
                    className="input-industrial font-mono text-sm"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Get your key from: <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener" className="text-cyan-400 hover:underline">SendGrid Settings</a>
                  </p>
                </div>

                <div>
                  <Label className="text-slate-400 mb-2 block">
                    Sender Email <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={settings.sender_email || ''}
                    onChange={(e) => setSettings({...settings, sender_email: e.target.value})}
                    placeholder="noreply@yourdomain.com"
                    className="input-industrial"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Must be verified in SendGrid. <a href="https://app.sendgrid.com/settings/sender_auth" target="_blank" rel="noopener" className="text-cyan-400 hover:underline">Verify sender</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-amber-900/20 border border-amber-500/50 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-amber-300">
                  <p className="font-semibold mb-1">🔒 Security Note:</p>
                  <p>API keys and secrets are stored securely in environment variables. Never share these credentials publicly.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end gap-2 pb-8">
          <Button
            variant="outline"
            onClick={handleReset}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset to Default
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default SuperAdminSettingsPage;
